import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import tools from "../../../helpers/tools";
import { estAdministrateurOperateur, estUtilisateurOperateur } from "../../../helpers/rolesManager";
import ChoixChips from "../../common/ChoixChips/ChoixChips";
import { DataTable, DateCell, EmptyState, Pill } from "../../common/DataList";
import ModaleCompteOperateur from "./ModaleCompteOperateur";
import { listerSitesDeOperateur, renvoyerLienActivationCompteOperateur } from "./operateursApi";
import {
  compterParRole,
  estDesactive,
  estEnAttente,
  etatCompteOperateur,
  filtrerComptes,
  libelleRoleCompte,
  libelleSitesCompte,
  nomCompletCompte,
  resumeComptes,
} from "./comptesOperateur";

// ============================================================
// Onglet « Utilisateurs » de la fiche opérateur (CE-1407, maquette 10) : les
// comptes de la structure, filtrés localement (recherche, rôle, comptes
// désactivés masqués par défaut), avec leurs sites. Chaque compte s'ouvre sur
// sa fiche (FicheCompteOperateur), où se font modification, désactivation et
// réactivation. Ici : création en modale (Caf seule) et renvoi de
// l'invitation d'un compte en attente (Caf et administrateur d'opérateur).
// Les comptes sont chargés par la fiche opérateur (l'encart de mise en service
// les lit aussi) et rechargés après chaque action (`onComptesModifies`).
// ============================================================

const contratEchu = (compte) => {
  if (!compte.dateFinContrat) return false;
  const fin = new Date(compte.dateFinContrat);
  return !Number.isNaN(fin.getTime()) && fin.getTime() < Date.now() - 86400000;
};

const OngletUtilisateurs = ({ operateur, comptes, editeur, onComptesModifies }) => {
  const [sites, setSites] = useState([]);
  const [terme, setTerme] = useState("");
  const [role, setRole] = useState("");
  const [voirDesactives, setVoirDesactives] = useState(false);
  const [modaleOuverte, setModaleOuverte] = useState(false);
  const [renvoiEnCours, setRenvoiEnCours] = useState(null); // id du compte

  const editeurOperateur = estUtilisateurOperateur(editeur);
  const peutCreer = !editeurOperateur;
  const peutAgir = !editeurOperateur || estAdministrateurOperateur(editeur && editeur.role);

  useEffect(() => {
    listerSitesDeOperateur(operateur.id).then(setSites, () => setSites([]));
  }, [operateur.id]);

  const liste = useMemo(() => filtrerComptes(comptes, { terme, role, voirDesactives }), [comptes, terme, role, voirDesactives]);
  const effectifs = useMemo(() => compterParRole(comptes, voirDesactives), [comptes, voirDesactives]);
  const optionsRole = [
    { id: "", name: `Tous ${effectifs.tous}` },
    { id: "ADMIN_OPERATEUR", name: `Administrateurs ${effectifs.ADMIN_OPERATEUR}` },
    { id: "MANAGEUR_OPERATEUR", name: `Manageurs ${effectifs.MANAGEUR_OPERATEUR}` },
    { id: "AGENT_OPERATEUR", name: `Agents ${effectifs.AGENT_OPERATEUR}` },
  ];

  const lienFiche = (c) => `/operateurs/${operateur.id}/comptes/${c.id}`;

  const surCreation = (_fiche, email) => {
    setModaleOuverte(false);
    toast.success(`Invitation envoyée à ${email}.`);
    onComptesModifies();
  };

  const renvoyer = (compte) => {
    setRenvoiEnCours(compte.id);
    renvoyerLienActivationCompteOperateur(operateur.id, compte.id).then(
      () => {
        setRenvoiEnCours(null);
        toast.success(`L'invitation a été renvoyée à ${compte.email}.`);
        onComptesModifies();
      },
      (erreur) => {
        setRenvoiEnCours(null);
        toast.error(erreur.message || "Le renvoi de l'invitation a été refusé.");
      }
    );
  };

  const colonnes = [
    {
      key: "utilisateur",
      header: "Utilisateur",
      className: "col-recipient",
      render: (c) => {
        const nom = nomCompletCompte(c);
        // Sans prénom ni nom, l'e-mail tient lieu de nom : pas deux fois.
        return (
          <>
            <Link to={lienFiche(c)} className="recipient-name">
              {nom}
            </Link>
            {nom !== c.email && <span className="cell-secondary">{c.email}</span>}
          </>
        );
      },
    },
    {
      key: "role",
      header: "Rôle",
      render: (c) => (
        <>
          <span className="cell-primary">{libelleRoleCompte(c.role)}</span>
          {c.recoitAlertesPlanning && (
            <span className="cell-secondary">
              <i className="fa fa-bell" aria-hidden="true" /> Alertes de planning
            </span>
          )}
        </>
      ),
    },
    {
      key: "sites",
      header: "Sites",
      className: "col-sites-compte",
      render: (c) => libelleSitesCompte(c, sites, operateur.nom, { vueLimitee: editeurOperateur }),
    },
    {
      key: "compte",
      header: "Compte",
      render: (c) => {
        const etat = etatCompteOperateur(c);
        return (
          <>
            <Pill tone={etat.ton} icon={etat.icone}>
              {etat.court}
            </Pill>
            {etat.id === "attente" && c.lienActivationEnvoyeLe && (
              <span className="cell-secondary">le {tools.formatDate(c.lienActivationEnvoyeLe)}</span>
            )}
            {c.dateFinContrat && (
              <span className={`cell-secondary${contratEchu(c) ? " cell-note--warn" : ""}`}>
                fin de contrat le {tools.formatDate(c.dateFinContrat)}
              </span>
            )}
          </>
        );
      },
    },
    {
      key: "derniereConnexionLe",
      header: "Dernière connexion",
      className: "col-date",
      render: (c) => <DateCell value={c.derniereConnexionLe} empty="Jamais" />,
    },
    {
      key: "actions",
      header: "Actions",
      headerSrOnly: true,
      className: "col-actions",
      render: (c) => {
        const nom = nomCompletCompte(c);
        return (
          <span className="onglet-utilisateurs__actions">
            {peutAgir && estEnAttente(c) && (
              <button
                type="button"
                className="btn btn-ghost btn-icon"
                onClick={() => renvoyer(c)}
                disabled={renvoiEnCours === c.id}
                aria-label={`Renvoyer l’invitation à ${nom}`}
                title="Renvoyer l'invitation"
              >
                <i className="fa fa-envelope-open-text" aria-hidden="true" />
              </button>
            )}
            <Link
              to={lienFiche(c)}
              className="btn btn-ghost btn-icon"
              aria-label={`Ouvrir la fiche de ${nom}`}
              title="Ouvrir la fiche"
            >
              <i className="fa fa-arrow-right" aria-hidden="true" />
            </Link>
          </span>
        );
      },
    },
  ];

  const boutonAjouter = peutCreer ? (
    <div data-priv="crud_operateurs">
      <button type="button" className="btn btn-primary" onClick={() => setModaleOuverte(true)}>
        <i className="fa fa-user-plus mr-2" aria-hidden="true" />
        Ajouter un utilisateur
      </button>
    </div>
  ) : null;

  return (
    <div className="onglet-utilisateurs">
      <div className="onglet-utilisateurs__tete">
        <p className="fiche-operateur__secondaire">
          {resumeComptes(comptes)}
          {resumeComptes(comptes) ? ". " : ""}
          Un compte n'appartient qu'à une structure et ne voit que ses sites.
        </p>
        {boutonAjouter}
      </div>

      {!(comptes || []).length ? (
        <section className="list-card">
          <EmptyState
            icon="fa-users"
            title="Aucun utilisateur"
            hint={
              peutCreer
                ? "Commencez par l'administrateur de la structure : il pourra ensuite gérer ses manageurs et ses agents. Chaque utilisateur reçoit un e-mail d'activation."
                : "Aucun compte n'est encore rattaché à la structure."
            }
            action={peutCreer ? { label: "Ajouter un utilisateur", onClick: () => setModaleOuverte(true) } : null}
          />
        </section>
      ) : (
        <section className="list-card" aria-label="Comptes de la structure">
          <div className="onglet-utilisateurs__outils">
            <label className="onglet-utilisateurs__recherche">
              <i className="fa fa-search" aria-hidden="true" />
              <span className="sr-only">Rechercher un utilisateur par nom, prénom ou e-mail</span>
              <input
                type="search"
                className="form-control"
                placeholder="Nom, prénom ou e-mail…"
                value={terme}
                onChange={(e) => setTerme(e.target.value)}
                autoComplete="off"
              />
            </label>
            <ChoixChips
              name="role-comptes"
              legend="Rôle"
              legendSrOnly
              variant="segmente"
              options={optionsRole}
              value={role}
              onChange={(valeur) => setRole(String(valeur))}
            />
            <label className="interrupteur">
              <input
                type="checkbox"
                name="voirDesactives"
                checked={voirDesactives}
                onChange={(e) => setVoirDesactives(e.target.checked)}
              />
              Afficher les comptes désactivés
            </label>
          </div>
          <DataTable
            caption={`Comptes de ${operateur.nom}`}
            columns={colonnes}
            rows={liste}
            rowKey="id"
            rowClassName={(c) => (estDesactive(c) ? "row-compte-desactive" : undefined)}
            emptyState={{
              icon: "fa-filter",
              title: "Aucun utilisateur ne correspond",
              hint: "Changez de rôle ou videz la recherche.",
              action: {
                label: "Réinitialiser les filtres",
                onClick: () => {
                  setTerme("");
                  setRole("");
                },
              },
            }}
          />
        </section>
      )}

      {peutCreer && (
        <ModaleCompteOperateur
          ouvert={modaleOuverte}
          onFermer={() => setModaleOuverte(false)}
          operateur={operateur}
          sites={sites}
          onEnregistre={surCreation}
        />
      )}
    </div>
  );
};

export default OngletUtilisateurs;
